import React from 'react';
import * as Cesium from 'cesium';
import * as DOMPurify from 'dompurify';
import { Entity, ModelGraphics, PathGraphics, useCesium } from 'resium';
import { useIntl } from 'react-intl';
import { Types } from '@wheeebo/polaris-api';
const DRONE_MODEL_URI = '/quadrocopter.glb';
const HOVERBIKE_MODEL_URI = '/hoverbike.glb';
const DRONE_HEADING_ADJUSTMENT = Cesium.Math.PI_OVER_TWO;
const HOVERBIKE_HEADING_ADJUSTMENT = -Cesium.Math.PI_OVER_TWO;
function createPosition(waypoint) {
    return Cesium.Cartesian3.fromDegrees(waypoint.location.lng, waypoint.location.lat, waypoint.altitude);
}
function createProperty(start, flightPath, vehicleType) {
    let startTime = start;
    const position = new Cesium.SampledPositionProperty();
    const orientation = new Cesium.TimeIntervalCollectionProperty();
    const sorted = flightPath?.waypoints?.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    sorted?.forEach((e, i) => {
        const startWaypoint = e;
        const stopWaypoint = i === (flightPath.waypoints?.length || 0) - 1 ? e : sorted[i + 1];
        const startPosition = createPosition(startWaypoint);
        const stopPosition = createPosition(stopWaypoint);
        const distance = Cesium.Cartesian3.distance(startPosition, stopPosition);
        const duration = distance / (stopWaypoint.speed || 5);
        const stopTime = Cesium.JulianDate.addSeconds(startTime, duration, new Cesium.JulianDate());
        const headingAdjustment = vehicleType === Types.VehicleTypeDrone ? DRONE_HEADING_ADJUSTMENT : HOVERBIKE_HEADING_ADJUSTMENT;
        const heading = Math.atan2(stopWaypoint.location.lng - startWaypoint.location.lng, stopWaypoint.location.lat - startWaypoint.location.lat) + headingAdjustment;
        const hpr = new Cesium.HeadingPitchRoll(heading, 0, 0);
        const data = Cesium.Transforms.headingPitchRollQuaternion(startPosition, hpr);
        orientation.intervals.addInterval(new Cesium.TimeInterval({ start: startTime, stop: stopTime, data }));
        position.addSample(startTime, startPosition);
        startTime = stopTime;
    });
    return { position, orientation };
}
function FlightPathEntity({ flightPlan, flightPath, tracked, start, stop }) {
    const intl = useIntl();
    const color = tracked ? Cesium.Color.AQUA : Cesium.Color.CYAN;
    const property = createProperty(start.clone(), flightPath, flightPlan.vehicle?.vehicleType);
    const { viewer } = useCesium();
    const moveMapCenter = React.useCallback(() => {
        const entity = viewer?.entities.getById(flightPath.id);
        if (entity) {
            viewer?.flyTo(entity, { duration: 0, offset: new Cesium.HeadingPitchRange(Math.PI / 2, -Math.PI / 6, 25) });
        }
    }, [flightPath.id, viewer]);
    DOMPurify.setConfig({ ALLOWED_TAGS: [] });
    const description = [
        `<table><tr><td>Flight path</th><td>${DOMPurify.sanitize(flightPath.name)}</td></tr>`,
        `<tr><td>Operator</th><td>${DOMPurify.sanitize(flightPlan.operator?.name)}</td></tr>`,
        `<tr><td>Vehicle</th><td>${DOMPurify.sanitize(flightPlan.vehicle?.name)}</td></tr>`,
        `<tr><td>Vehicle Type</th><td>${DOMPurify.sanitize(flightPlan.vehicle?.vehicleType)}</td></tr>`,
        `<tr><td>Start date</th><td>${intl.formatDate(flightPlan.startAt, {
            dateStyle: 'short',
            timeStyle: 'short',
        })}    </td></tr>`,
        `<tr><td>End date</th><td>${intl.formatDate(flightPlan.endAt, {
            dateStyle: 'short',
            timeStyle: 'short',
        })}</td></tr>`,
        '</table>',
    ];
    React.useEffect(() => {
        if (tracked) {
            moveMapCenter();
        }
    }, [moveMapCenter, tracked]);
    return (React.createElement(Entity, { id: flightPath.id, tracked: tracked, name: "Flight Path", description: description.join(''), position: property.position, onClick: moveMapCenter, orientation: property.orientation, availability: new Cesium.TimeIntervalCollection([new Cesium.TimeInterval({ start, stop })]) },
        flightPlan.vehicle?.vehicleType === Types.VehicleTypeDrone ? (React.createElement(ModelGraphics, { uri: DRONE_MODEL_URI, runAnimations: true, scale: 1 / 250, maximumScale: 1.25, minimumPixelSize: 32 })) : (React.createElement(ModelGraphics, { uri: HOVERBIKE_MODEL_URI, runAnimations: true, scale: 2, maximumScale: 125, minimumPixelSize: 32 })),
        React.createElement(PathGraphics, { resolution: 1, width: 20, material: new Cesium.PolylineGlowMaterialProperty({ glowPower: 0.1, color }) })));
}
export default React.memo(FlightPathEntity);
